<template>
    <aside class="filter-sidebar">
        <div class="sidebar-header">
            <h3>Filters</h3>
            <button v-if="hasActive" @click="emit('reset')" class="reset-btn">Reset</button>
        </div>

        <FilterSection title="Grade">
            <label v-for="opt in facets.grade" :key="opt.tier" class="check-row">
                <input
                    type="checkbox"
                    :checked="filters.grade.includes(opt.tier)"
                    @change="emit('toggle', 'grade', opt.tier)"
                />
                <span class="check-name" :class="`grade-${opt.tier.toLowerCase()}`">{{ opt.tier }}</span>
                <span class="check-count">{{ opt.count }}</span>
            </label>
        </FilterSection>

        <FilterSection title="For Moms">
            <label v-for="opt in audienceOptions" :key="opt.bucket" class="check-row">
                <input
                    type="checkbox"
                    :checked="filters.audience.includes(opt.bucket)"
                    @change="emit('toggle', 'audience', opt.bucket)"
                />
                <span class="check-name">{{ opt.label }}</span>
                <span class="check-count">{{ opt.count }}</span>
            </label>
        </FilterSection>

        <FilterSection title="Price">
            <div class="range-inputs">
                <input type="number" min="0" placeholder="Min $"
                       :value="filters.priceMin ?? ''"
                       @input="emit('updateFilter', 'priceMin', toNumOrNull(($event.target as HTMLInputElement).value))" />
                <span>–</span>
                <input type="number" min="0" placeholder="Max $"
                       :value="filters.priceMax ?? ''"
                       @input="emit('updateFilter', 'priceMax', toNumOrNull(($event.target as HTMLInputElement).value))" />
            </div>
            <div class="bucket-hint" v-if="facets.priceBuckets.length">
                <span v-for="b in facets.priceBuckets" :key="b.range" class="bucket-pill">
                    {{ b.range }} <em>{{ b.count }}</em>
                </span>
            </div>
        </FilterSection>

        <FilterSection title="% Off">
            <div class="range-inputs">
                <input type="number" min="0" max="100" placeholder="Min %"
                       :value="filters.pctOffMin ?? ''"
                       @input="emit('updateFilter', 'pctOffMin', toNumOrNull(($event.target as HTMLInputElement).value))" />
                <span>–</span>
                <input type="number" min="0" max="100" placeholder="Max %"
                       :value="filters.pctOffMax ?? ''"
                       @input="emit('updateFilter', 'pctOffMax', toNumOrNull(($event.target as HTMLInputElement).value))" />
            </div>
        </FilterSection>

        <FilterSection title="Category" v-if="facets.category.length || filters.category != null">
            <div v-if="filters.category != null" class="cat-breadcrumb">
                <button class="crumb-link" @click="emit('setCategory', null)">All</button>
                <template v-for="(a, i) in facets.categoryAncestors" :key="a.id">
                    <span class="crumb-sep">›</span>
                    <button
                        v-if="i < facets.categoryAncestors.length - 1"
                        class="crumb-link"
                        @click="emit('setCategory', a.id)"
                    >{{ a.name }}</button>
                    <span v-else class="crumb-current">{{ a.name }}</span>
                </template>
            </div>
            <button
                v-for="opt in facets.category"
                :key="opt.id"
                type="button"
                class="cat-row"
                @click="emit('setCategory', opt.id)"
            >
                <span class="check-name">{{ opt.name }}</span>
                <span class="check-count">{{ opt.count }}</span>
                <span class="cat-arrow">›</span>
            </button>
            <div v-if="!facets.category.length && filters.category != null" class="cat-leaf-hint">
                No subcategories — showing all deals at this level.
            </div>
        </FilterSection>

        <FilterSection title="Brand" v-if="facets.brand.length">
            <label v-for="opt in facets.brand" :key="opt.name" class="check-row">
                <input
                    type="checkbox"
                    :checked="filters.brand.includes(opt.name)"
                    @change="emit('toggle', 'brand', opt.name)"
                />
                <span class="check-name">{{ opt.name }}</span>
                <span class="check-count">{{ opt.count }}</span>
            </label>
        </FilterSection>

        <FilterSection title="Retailer" v-if="facets.retailer.length > 1">
            <label v-for="opt in facets.retailer" :key="opt.name" class="check-row">
                <input
                    type="checkbox"
                    :checked="filters.retailer.includes(opt.name)"
                    @change="emit('toggle', 'retailer', opt.name)"
                />
                <span class="check-name">{{ opt.name }}</span>
                <span class="check-count">{{ opt.count }}</span>
            </label>
        </FilterSection>

        <FilterSection title="Discount type" v-if="facets.discount.length">
            <label v-for="opt in facets.discount" :key="opt.code" class="check-row">
                <input
                    type="checkbox"
                    :checked="filters.discount.includes(opt.code)"
                    @change="emit('toggle', 'discount', opt.code)"
                />
                <span class="check-name">{{ opt.name }}</span>
                <span class="check-count">{{ opt.count }}</span>
            </label>
        </FilterSection>

        <FilterSection title="Flags">
            <label class="check-row">
                <input type="checkbox" :checked="filters.flags.includes('amazonsChoice')"
                       @change="emit('toggle', 'flags', 'amazonsChoice')" />
                <span class="check-name">Amazon's Choice</span>
                <span class="check-count">{{ facets.flags.amazonsChoice }}</span>
            </label>
            <label class="check-row">
                <input type="checkbox" :checked="filters.flags.includes('bestSeller')"
                       @change="emit('toggle', 'flags', 'bestSeller')" />
                <span class="check-name">Best Seller</span>
                <span class="check-count">{{ facets.flags.bestSeller }}</span>
            </label>
            <label class="check-row">
                <input type="checkbox" :checked="filters.flags.includes('prime')"
                       @change="emit('toggle', 'flags', 'prime')" />
                <span class="check-name">Prime eligible</span>
                <span class="check-count">{{ facets.flags.prime }}</span>
            </label>
            <label class="check-row">
                <input type="checkbox" :checked="filters.flags.includes('soldByAmazon')"
                       @change="emit('toggle', 'flags', 'soldByAmazon')" />
                <span class="check-name">Sold by Amazon</span>
                <span class="check-count">{{ facets.flags.soldByAmazon }}</span>
            </label>
        </FilterSection>

        <FilterSection title="Other">
            <label class="check-row">
                <input type="checkbox" :checked="filters.inStock"
                       @change="emit('updateFilter', 'inStock', ($event.target as HTMLInputElement).checked)" />
                <span class="check-name">In stock</span>
            </label>
            <label class="check-row">
                <input type="checkbox" :checked="filters.hasPromo"
                       @change="emit('updateFilter', 'hasPromo', ($event.target as HTMLInputElement).checked)" />
                <span class="check-name">Has promo / coupon</span>
            </label>
        </FilterSection>
    </aside>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, ref } from 'vue'
import type { SearchFilters, SearchFacets } from '@/composables/useDealsSearch'

const props = defineProps<{
    filters: SearchFilters
    facets: SearchFacets
    hasActive: boolean
}>()
const emit = defineEmits<{
    (e: 'toggle', dim: keyof SearchFilters, value: string | number): void
    (e: 'updateFilter', key: keyof SearchFilters, value: any): void
    (e: 'setCategory', id: number | null): void
    (e: 'reset'): void
}>()

// Lightweight collapsible section — defined inline so we don't ship an
// extra file just for a div + button pair.
const FilterSection = defineComponent({
    name: 'FilterSection',
    props: { title: { type: String, required: true } },
    setup(p, { slots }) {
        const open = ref(true)
        return () =>
            h('div', { class: 'filter-section' }, [
                h('button', {
                    class: 'section-header',
                    onClick: () => (open.value = !open.value),
                    type: 'button',
                }, [
                    h('span', null, p.title),
                    h('span', { class: 'section-toggle' }, open.value ? '−' : '+'),
                ]),
                open.value ? h('div', { class: 'section-body' }, slots.default?.()) : null,
            ])
    },
})

const AUDIENCE_LABELS: Record<number, string> = {
    80: '🔥 Great for moms (80+)',
    60: '👍 Good for moms (60–79)',
    40: '🤷 Maybe (40–59)',
    20: '👎 Probably not (20–39)',
    0:  '❄️ Not for moms (<20)',
}
const audienceOptions = computed(() => {
    const counts = new Map(props.facets.audience.map(a => [a.bucket, a.count]))
    return [80, 60, 40, 20, 0].map(b => ({
        bucket: b,
        label: AUDIENCE_LABELS[b],
        count: counts.get(b) ?? 0,
    }))
})

const toNumOrNull = (s: string): number | null => {
    if (s === '' || s == null) return null
    const n = Number(s)
    return isNaN(n) ? null : n
}
</script>

<style scoped>
.filter-sidebar {
    width: 240px;
    flex-shrink: 0;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fff;
    padding: 12px 14px;
    height: fit-content;
    position: sticky;
    top: 16px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
}

.sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #eee;
}
.sidebar-header h3 { margin: 0; font-size: 15px; color: #222; }
.reset-btn {
    background: none;
    border: 1px solid #cfd8dc;
    padding: 3px 10px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    color: #555;
}
.reset-btn:hover { background: #f5f5f5; }

.filter-section { border-top: 1px solid #eee; }
.filter-section:first-of-type { border-top: none; }

:deep(.section-header) {
    width: 100%;
    background: none;
    border: none;
    padding: 10px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #222;
    text-align: left;
}
:deep(.section-header:hover) { color: #1565c0; }
:deep(.section-toggle) { color: #999; font-weight: 400; font-size: 14px; }
:deep(.section-body) { padding: 0 0 10px 0; }

.check-row {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 4px 2px;
    cursor: pointer;
    font-size: 12px;
    color: #444;
    user-select: none;
}
.check-row:hover { color: #1565c0; }
.check-row input[type="checkbox"] { margin: 0; cursor: pointer; }
.check-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.check-count {
    color: #999;
    font-size: 11px;
    flex-shrink: 0;
}

.check-name.grade-great   { color: #2e7d32; font-weight: 600; }
.check-name.grade-good    { color: #43a047; font-weight: 600; }
.check-name.grade-sale    { color: #8a6d00; font-weight: 600; }
.check-name.grade-regular { color: #555; }

.range-inputs {
    display: flex;
    align-items: center;
    gap: 6px;
}
.range-inputs input {
    width: 80px;
    padding: 5px 7px;
    border: 1px solid #cfd8dc;
    border-radius: 3px;
    font-size: 12px;
}
.range-inputs input:focus { outline: none; border-color: #1565c0; }
.range-inputs span { color: #999; }

.bucket-hint {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}
.bucket-pill {
    background: #f5f5f5;
    color: #666;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
}
.bucket-pill em { color: #999; font-style: normal; }

.cat-breadcrumb {
    font-size: 11px;
    color: #666;
    margin-bottom: 6px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
}
.crumb-link {
    background: none;
    border: none;
    color: #1565c0;
    cursor: pointer;
    padding: 0;
    font-size: 11px;
    font-weight: 600;
}
.crumb-link:hover { text-decoration: underline; }
.crumb-sep { color: #bbb; }
.crumb-current { color: #222; font-weight: 600; }

.cat-row {
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    padding: 6px 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    color: #444;
    border-radius: 3px;
}
.cat-row:hover { background: #f5f9ff; color: #1565c0; }
.cat-row .cat-arrow { color: #bbb; font-size: 14px; flex-shrink: 0; }
.cat-row:hover .cat-arrow { color: #1565c0; }

.cat-leaf-hint {
    font-size: 11px;
    color: #888;
    font-style: italic;
    padding: 6px 4px;
}
</style>
